import * as React from "react";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
  ColumnDef,
  SortingState,
  VisibilityState,
} from "@tanstack/react-table";

/** Optional: per-column meta for alignment/nowrap */
declare module "@tanstack/react-table" {
  interface ColumnMeta<TData, TValue> {
    /** Right-align + tabular numbers (default false) */
    numeric?: boolean;
    /** Prevent wrapping in cells/headers (default true) */
    nowrap?: boolean;
  }
}

export type DataTableProps<TData, TValue> = {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  Toolbar?: React.ComponentType<{ table: ReturnType<typeof useReactTable<TData>> }>;
  Footer?: React.ComponentType<{ table: ReturnType<typeof useReactTable<TData>> }>;
  initialSorting?: SortingState;
  initialVisibility?: VisibilityState;

  /** default rows per page (fallback is TanStack default = 10) */
  pageSizeDefault?: number;

  /** options shown in "Rows per page" selector (default [10,20,50,100]) */
  pageSizeOptions?: number[];

  /** Whether to render the per-column filter row (default true). */
  showColumnFilters?: boolean;
};

export function DataTable<TData, TValue>({
  columns,
  data,
  Toolbar,
  Footer,
  initialSorting,
  initialVisibility,
  pageSizeDefault,
  pageSizeOptions = [10, 20, 50, 100],
  showColumnFilters = true,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>(initialSorting ?? []);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(
    initialVisibility ?? {}
  );
  const [globalFilter, setGlobalFilter] = React.useState("");

  const table = useReactTable({
    data,
    columns,
    state: { sorting, columnVisibility, globalFilter },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    columnResizeMode: "onChange",
    enableColumnResizing: true,
    autoResetAll: false,
    debugTable: false,
  });

  React.useEffect(() => {
    if (
      typeof pageSizeDefault === "number" &&
      Number.isFinite(pageSizeDefault) &&
      pageSizeDefault > 0 &&
      table.getState().pagination.pageSize !== pageSizeDefault
    ) {
      table.setPageSize(pageSizeDefault);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSizeDefault]);

  const leafCols = table.getAllLeafColumns();

  const cellClasses = (
    colMeta: { numeric?: boolean; nowrap?: boolean } | undefined,
    isHeader = false
  ) => {
    const numeric = Boolean(colMeta?.numeric);
    const nowrap = colMeta?.nowrap !== false;

    return [
      "px-3 py-2 align-middle",
      nowrap ? "whitespace-nowrap" : "whitespace-normal",
      numeric ? "text-right tabular-nums" : "text-left",
      isHeader ? "font-medium text-muted-foreground" : "text-foreground",
    ].join(" ");
  };

  return (
    <div className="w-full">
      {Toolbar ? <Toolbar table={table} /> : null}

      <div className="relative overflow-hidden rounded-xl border border-border bg-card">
        <div className="overflow-auto">
          <table className="w-full table-fixed text-sm">
            <colgroup>
              {leafCols.map((col) => (
                <col key={col.id} style={{ width: col.getSize() }} />
              ))}
            </colgroup>

            <thead className="sticky top-0 z-10 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80 shadow-sm">
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((header) => {
                    const canSort = header.column.getCanSort();
                    const meta = header.column.columnDef.meta as
                      | { numeric?: boolean; nowrap?: boolean }
                      | undefined;

                    return (
                      <th
                        key={header.id}
                        className={`${cellClasses(meta, true)} relative select-none border-b border-border`}
                        style={{ width: header.getSize() }}
                      >
                        {header.isPlaceholder ? null : (
                          <button
                            type="button"
                            className={`inline-flex items-center gap-1 ${
                              canSort ? "transition-opacity hover:opacity-80" : "cursor-default"
                            }`}
                            onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {canSort ? (
                              <span className="text-xs text-muted-foreground">
                                {({ asc: "↑", desc: "↓" } as Record<string, string>)[
                                  header.column.getIsSorted() as string
                                ] ?? ""}
                              </span>
                            ) : null}
                          </button>
                        )}

                        {header.column.getCanResize() ? (
                          <div
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            className={`absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none ${
                              header.column.getIsResizing() ? "bg-primary/60" : "bg-transparent"
                            }`}
                          />
                        ) : null}
                      </th>
                    );
                  })}
                </tr>
              ))}

              {showColumnFilters ? (
                <tr>
                  {leafCols.map((col) => (
                    <th
                      key={`filter-${col.id}`}
                      className="border-b border-border bg-muted/40 px-2 py-1"
                    >
                      {col.getCanFilter() ? (
                        <input
                          className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                          placeholder={`Filter ${String(col.id)}`}
                          value={(col.getFilterValue() as string) ?? ""}
                          onChange={(e) => col.setFilterValue(e.target.value)}
                        />
                      ) : null}
                    </th>
                  ))}
                </tr>
              ) : null}
            </thead>

            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-border transition-colors odd:bg-background even:bg-muted/20 hover:bg-muted/40"
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta as
                      | { numeric?: boolean; nowrap?: boolean }
                      | undefined;

                    return (
                      <td key={cell.id} className={`${cellClasses(meta)} border-b border-border`}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              ))}

              {table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={leafCols.length}
                    className="px-3 py-6 text-center text-muted-foreground"
                  >
                    No rows match the current filters.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-border bg-card px-2 py-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-md border border-border bg-background px-2 py-1 text-foreground transition-colors disabled:opacity-40"
              onClick={() => table.firstPage()}
              disabled={!table.getCanPreviousPage()}
            >
              « First
            </button>

            <button
              type="button"
              className="rounded-md border border-border bg-background px-2 py-1 text-foreground transition-colors disabled:opacity-40"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              ‹ Prev
            </button>

            <span>
              Page <strong>{table.getState().pagination.pageIndex + 1}</strong> of{" "}
              {table.getPageCount()}
            </span>

            <button
              type="button"
              className="rounded-md border border-border bg-background px-2 py-1 text-foreground transition-colors disabled:opacity-40"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next ›
            </button>

            <button
              type="button"
              className="rounded-md border border-border bg-background px-2 py-1 text-foreground transition-colors disabled:opacity-40"
              onClick={() => table.lastPage()}
              disabled={!table.getCanNextPage()}
            >
              Last »
            </button>
          </div>

          <div className="flex items-center gap-2">
            <span>Rows per page</span>
            <select
              className="min-w-[4.25rem] rounded-md border border-input bg-background px-2 py-1 text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
              value={table.getState().pagination.pageSize}
              onChange={(e) => table.setPageSize(Number(e.target.value))}
              aria-label="Rows per page"
            >
              {(pageSizeOptions && pageSizeOptions.length
                ? pageSizeOptions
                : [10, 20, 50, 100]
              ).map((ps) => (
                <option key={ps} value={ps}>
                  {ps}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {Footer ? <Footer table={table} /> : null}
    </div>
  );
}